import PropTypes from 'prop-types';
import { useState } from 'react';
import { useData } from '../hooks/useData';
import { useError } from '../hooks/useError';
import { useExistingTrial } from '../hooks/useExistingTrial';
import { useLocation } from '../hooks/useLocation';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { useTrial } from '../hooks/useTrial';
import { useVisibility } from '../hooks/useVisibility';
import Customization from './Customization';
import Error from './Error';
import ExistingTrialNotification from './ExistingTrialNotification';
import Loading from './Loading';
import Registration from './Registration';
import Verification from './Verification';

export default function App(props) {
	const [key, setKey] = useState(0);
	const reloadApp = () => setKey((key) => key + 1);
	const { ref, isVisible } = useVisibility();

	return (
		<div className="trial" ref={ref}>
			{isVisible && <Trial key={key} {...props} reloadApp={reloadApp} />}
		</div>
	);
}

function Trial({ dataUrl, isMobile, onStatusChange = () => {}, reloadApp, setSceneData = () => {} }) {
	const { setError, errorMessage } = useError(getLabel);
	const { data, loading } = useData(dataUrl, setError);
	const { getRecaptchaToken } = useRecaptcha(data?.recaptchaSiteKey);
	const { region } = useLocation(data?.apiRoutes.region);
	const { hasExistingTrial, forgetTrialSystem, rememberTrialSystem } = useExistingTrial();
	const {
		clearTrialState,
		onCompleteCustomization,
		onRegistrationFormSubmit,
		resendActivationEmail,
		trialActivationCode,
		trialEmailAddress,
		trialLoading,
		trialSystemId,
	} = useTrial({
		data,
		getLabel,
		getRecaptchaToken,
		isMobile,
		onStatusChange,
		rememberTrialSystem,
	});

	const reload = () => {
		clearTrialState();
		reloadApp();
	};

	function getLabel(key) {
		return data?.labels[key] || '';
	}

	if (loading) {
		return null;
	}

	if (errorMessage) {
		return <Error data={data} getLabel={getLabel} message={errorMessage} reload={reload} setSceneData={setSceneData} />;
	}

	if (trialLoading) {
		return <Loading data={data} getLabel={getLabel} predictedLoadTime={data.predictedLoadTime} />;
	}

	if (hasExistingTrial && data.apiRoutes.appLogin) {
		return <ExistingTrialNotification apiRoutes={data.apiRoutes} forgetTrialSystem={forgetTrialSystem} getLabel={getLabel} reload={reload} />;
	}

	if (!trialSystemId) {
		return (
			<Registration
				data={data}
				getLabel={getLabel}
				getRecaptchaToken={getRecaptchaToken}
				isMobile={isMobile}
				onFormSubmit={onRegistrationFormSubmit}
				region={region}
				setError={setError}
				setSceneData={setSceneData}
			/>
		);
	}

	if (!trialActivationCode) {
		return (
			<Verification
				data={data}
				emailAddress={trialEmailAddress}
				getLabel={getLabel}
				isMobile={isMobile}
				reload={reload}
				resendActivationEmail={resendActivationEmail}
				setSceneData={setSceneData}
			/>
		);
	} else {
		return <Customization data={data} getLabel={getLabel} onCompleteCustomization={onCompleteCustomization} setSceneData={setSceneData} />;
	}
}

Trial.propTypes = {
	dataUrl: PropTypes.string.isRequired,
	isMobile: PropTypes.bool.isRequired,
	onStatusChange: PropTypes.func,
	reloadApp: PropTypes.func.isRequired,
	setSceneData: PropTypes.func,
};
